import React from "react";
import { useTranslation } from "react-i18next";
import { CustomInput } from "reactstrap";

// Inline styles for cursor on switch labels
const switchStyles = `
  .consent-switch .custom-control-label { cursor: pointer; }
  .consent-switch .custom-control-input:disabled ~ .custom-control-label { cursor: not-allowed; }
`;

// Consent rows - each row has a category with optional email and push keys
const CONSENT_ROWS = [
  { category: "newsletter", email: "NEWSLETTER_EMAILS", push: null },
  { category: "promotional", email: "PROMOTIONAL_EMAILS", push: "PROMOTIONAL_NOTIFICATIONS" },
  { category: "modelWarnings", email: "BOT_WARNING_EMAILS", push: "BOT_WARNING_NOTIFICATIONS" },
  { category: "modelEvents", email: "BOT_EVENT_EMAILS", push: "BOT_EVENT_NOTIFICATIONS" },
  { category: "weeklySummary", email: "SUMMARY_EMAILS", push: "SUMMARY_NOTIFICATIONS" },
];

/**
 * ConsentsForm - Reusable form component for managing notification consents
 */
interface ConsentsFormProps {
  consents?: Record<string, boolean | undefined>;
  onChange: (key: string, value: boolean) => void;
  disabled?: boolean;
  loading?: boolean;
}

const ConsentsForm = ({ consents = {}, onChange, disabled = false, loading = false }: ConsentsFormProps) => {
  const { t } = useTranslation("dashboard");

  const handleChange = (key: string | null) => {
    if (!key) return;
    onChange(key, !consents[key]);
  };

  const renderToggle = (key: string | null) => {
    if (!key) return <div style={{ width: 50 }}></div>;

    const isChecked = consents[key] ?? true;

    return (
      <CustomInput
        type="switch"
        id={key}
        className="consent-switch"
        checked={isChecked}
        onChange={() => handleChange(key)}
        disabled={disabled || loading}
        label=""
      />
    );
  };

  return (
    <div className={loading ? "opacity-50" : ""}>
      <style>{switchStyles}</style>
      {/* Header */}
      <div className="d-flex align-items-center py-3 border-bottom">
        <div className="flex-grow-1"></div>
        <div className="text-center" style={{ width: 80 }}>
          <i className="fas fa-envelope text-muted mr-1"></i>
          <small className="text-muted font-weight-bold">{t("consents.headers.email")}</small>
        </div>
        <div className="text-center" style={{ width: 80 }}>
          <i className="fas fa-bell text-muted mr-1"></i>
          <small className="text-muted font-weight-bold">{t("consents.headers.push")}</small>
        </div>
      </div>

      {/* Rows */}
      {CONSENT_ROWS.map((row) => (
        <div key={row.category} className="d-flex align-items-center py-3 border-bottom">
          <div className="flex-grow-1">
            <span>{t(`consents.categories.${row.category}`)}</span>
          </div>
          <div className="d-flex justify-content-center" style={{ width: 80 }}>
            {renderToggle(row.email)}
          </div>
          <div className="d-flex justify-content-center" style={{ width: 80 }}>
            {renderToggle(row.push)}
          </div>
        </div>
      ))}
    </div>
  );
};

export default ConsentsForm;
